import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";

import { Store } from "shared/src/stores/Store";
import { determineClosestStoreByLocation } from "../clients/StoreLocationClient";
import TrackingUtil, { trackStructuredEvent } from "shared/src/utils/TrackingUtil";

type Status = "initial" | "loading" | "success" | "error";

function getGeoLocation(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
            maximumAge: 60000,
            timeout: 5000,
            enableHighAccuracy: true
        })
    );
}

export class ClosestStoreStoreImpl extends Store {
    status: Status = "initial";
    storeNumber: string | null = null;

    constructor() {
        super();
    }

    maybeSwitchToClosestStore() {
        if (!CountryUtil.getOptionalConfig()?.shouldUseClosestStore) {
            return;
        }
        if (SessionStore.getSelectedStoreId()) {
            // Store already explicitly selected
            return;
        }
        // We're intentionally racing with the SessionStore here. In the ideal
        // case, we finish this while the SessionStore is still pending.
        if (this.status === "initial") {
            this.loadClosestStore();
        } else if (this.storeNumber) {
            trackStructuredEvent("storeSelector.geolocation", {
                tags: { status: "success", storeId: this.storeNumber }
            });
            SessionStore.setSelectedStoreId(this.storeNumber);
        }
    }

    async loadClosestStore() {
        if (this.status !== "initial") {
            return;
        }
        this.status = "loading";
        let location: GeolocationPosition | null = null;
        this.trigger();
        try {
            location = await getGeoLocation();
        } catch (_e: any) {
            if (_e.code === _e.PERMISSION_DENIED) {
                trackStructuredEvent("storeSelector.geolocation", { tags: { status: "blocked" } });
            } else {
                trackStructuredEvent("storeSelector.geolocation", { tags: { status: "error" } });
            }
            this.setDefaultStore();
        }
        try {
            const response = await determineClosestStoreByLocation(
                CountryUtil.getCountry(),
                location?.coords?.longitude,
                location?.coords?.latitude
            );
            this.storeNumber = response.storeNumber || SessionStore.getCustomerStore();
            this.status = "success";
            this.maybeSwitchToClosestStore();
            this.trigger();
        } catch (e) {
            this.setDefaultStore();
            TrackingUtil.trackUiErrorSd("ClosestStoreStore", "Determining closest store failed.", e);
            this.status = "error";
            this.trigger();
        }
    }

    setDefaultStore() {
        const store = SessionStore.getCustomerStore();
        if (store) {
            SessionStore.setSelectedStoreId(store);
        }
    }

    isLoading() {
        return this.status === "loading";
    }

    getClosestStore() {
        return this.storeNumber;
    }
}

export const ClosestStoreStore = new ClosestStoreStoreImpl();
